import { BrowserModule } from '@angular/platform-browser';
import { CurrencyPipe } from '@angular/common';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { GooglePlaceModule } from 'ngx-google-places-autocomplete';
import { NgxsModule } from '@ngxs/store';
import { NgSelectModule } from '@ng-select/ng-select';
import { CreditCardDirectivesModule } from 'angular-cc-library';
import { NgAutonumericModule } from '@angularfy/ng-autonumeric';

import { AppComponent } from './app.component';
import { AppRouting } from './app.routing';
import { DonationAmountComponent } from './dynamic-section/donation-amount.component';
import { PaymentTypeComponent } from './dynamic-section/payment-type.component';
import { RecurringComponent } from './dynamic-section/recurring.component';
import { CorporateMatchingComponent } from './dynamic-section/corporate-matching.component';
import { DoubleTheDonationComponent } from './dynamic-section/double-the-donation.component';
import { DesignationsComponent } from './dynamic-section/designations.component';
import { DesignationMoreInfoComponent } from './dynamic-section/designation-more-info.component';
import { GiftComponent } from './dynamic-section/gift.component';
import { TributeComponent } from './dynamic-section/tribute.component';
import { BillingInfoComponent } from './dynamic-section/billing-info.component';
import { PaymentInfoComponent } from './dynamic-section/payment-info.component';
import { OptInsComponent } from './static-section/opt-ins.component';
import { GiveButtonComponent } from './static-section/give-button.component';
import { ThankYouComponent } from './thank-you/thank-you.component';

import { DesignationState } from './transaction-store/designation';
import { BillingInfoState } from './transaction-store/billing-info';
import { DonationState } from './transaction-store/donation';
import { OptInsState } from './transaction-store/opt-ins';
import { PaymentInfoState } from './transaction-store/payment-info';
import { RecurringState } from './transaction-store/recurring';
import { TransactionState } from './transaction-store/transaction';
import { CorporateMatchingState } from './transaction-store/corporate-matching';
import { MultiDesignationModalComponent } from './static-section/multi-designation-modal.component';
import { TextFieldComponent } from './shared/fields/text-field.component';
import { PhoneFieldComponent } from './shared/fields/phone-field.component';
import { ZipFieldComponent } from './shared/fields/zip-field.component';
import { FloatingLabelDirective } from './shared/input-directives/floating-label.directive';
import { SelectFieldComponent } from './shared/fields/select-field.component';
import { DateFieldComponent } from './shared/fields/date-field.component';
import { OptionButtonComponent } from './shared/option-button.component';
import { AddressBlockComponent } from './shared/forms/address-block.component';
import { ImpactComponent } from './static-section/impact.component';
import { TestimonialComponent } from './static-section/testimonial.component';
import { HttpClientModule } from '@angular/common/http';
import { ApiService } from './api/api.service';
import { DonateAPIService } from './api/donate.service';
import { CorporateAPIService } from './api/corporate-api.service';
import { TributeInfoState } from './transaction-store/tribute-info';
import { AddressFieldComponent } from './shared/fields/address-field.component';
import { LoginComponent } from './user/login.component';
import { LoginState } from './user/login.state';
import { AuthService } from './api/auth.service';
import { IPService } from './api/ip.service';
import { CvvFieldComponent } from './shared/fields/cvv-field.component';
import { CardFieldComponent } from './shared/fields/card-field.component';
import { CcIndicatorsComponent } from './shared/cc-indicators.component';
import { CreditCardDirective } from './shared/input-directives/credit-card.directive';
import { NumberFieldComponent } from './shared/fields/number-field.component';
import { CurrencyFieldComponent } from './shared/fields/currency-field.component';
import { UserService } from './api/user.service';
import { MinValidatorDirective } from './shared/input-directives/min-validator.directive';
import { MaxValidatorDirective } from './shared/input-directives/max-validator.directive';
import { DatadogErrorHandler } from './error.handler';
import { CustomNotesComponent } from './dynamic-section/custom-notes.component';
import { FooterComponent } from './footer.component';
import { GiftState } from './transaction-store/gift';
import { RegisterModalComponent } from './thank-you/register-modal.component';
import { PasswordDirective } from './shared/input-directives/password.directive';
import { PasswordFieldComponent } from './shared/fields/password-field.component';
import { EcheckHelpComponent } from './static-section/echeck-help.component';
import { HelperService } from './shared/helper.service';
import { MessageService } from './core/message.service';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { PaymentMethodComponent } from './shared/forms/payment-method.component';
import { GooglePayComponent } from './shared/forms/google-pay.component';
import { PayPalCheckoutComponent } from './shared/forms/paypal-checkout.component';
import { Sdk } from './core/sdk.service';

@NgModule({
  declarations: [
    AppComponent,
    DonationAmountComponent,
    PaymentTypeComponent,
    RecurringComponent,
    CorporateMatchingComponent,
    DoubleTheDonationComponent,
    DesignationsComponent,
    DesignationMoreInfoComponent,
    GiftComponent,
    TributeComponent,
    BillingInfoComponent,
    PaymentInfoComponent,
    OptInsComponent,
    GiveButtonComponent,
    ThankYouComponent,
    MultiDesignationModalComponent,
    TextFieldComponent,
    PhoneFieldComponent,
    ZipFieldComponent,
    FloatingLabelDirective,
    SelectFieldComponent,
    DateFieldComponent,
    OptionButtonComponent,
    AddressBlockComponent,
    ImpactComponent,
    TestimonialComponent,
    AddressFieldComponent,
    LoginComponent,
    CvvFieldComponent,
    CardFieldComponent,
    CcIndicatorsComponent,
    CreditCardDirective,
    NumberFieldComponent,
    CurrencyFieldComponent,
    MinValidatorDirective,
    MaxValidatorDirective,
    CustomNotesComponent,
    FooterComponent,
    RegisterModalComponent,
    EcheckHelpComponent,
    PasswordDirective,
    PasswordFieldComponent,
    PaymentMethodComponent,
    GooglePayComponent,
    PayPalCheckoutComponent
  ],
  entryComponents: [
    AppComponent,
    DonationAmountComponent,
    PaymentTypeComponent,
    RecurringComponent,
    CorporateMatchingComponent,
    DoubleTheDonationComponent,
    DesignationsComponent,
    GiftComponent,
    TributeComponent,
    BillingInfoComponent,
    PaymentInfoComponent,
    OptInsComponent,
    GiveButtonComponent,
    ThankYouComponent,
    MultiDesignationModalComponent,
    ImpactComponent,
    TestimonialComponent,
    LoginComponent,
    CustomNotesComponent,
    FooterComponent,
    RegisterModalComponent,
    EcheckHelpComponent
  ],
  imports: [
    NgAutonumericModule,
    HttpClientModule,
    BrowserModule,
    AppRouting,
    FormsModule,
    NgbModule,
    NgSelectModule,
    GooglePlaceModule,
    Ng2TelInputModule,
    CreditCardDirectivesModule,
    NgxsModule.forRoot([
      DesignationState,
      BillingInfoState,
      DonationState,
      OptInsState,
      PaymentInfoState,
      RecurringState,
      CorporateMatchingState,
      TributeInfoState,
      LoginState,
      TransactionState,
      GiftState
    ]),
  ],
  providers: [
    Sdk,
    CurrencyPipe,
    ApiService,
    UserService,
    DonateAPIService,
    CorporateAPIService,
    AuthService,
    IPService,
    HelperService,
    MessageService,
    { provide: ErrorHandler, useClass: DatadogErrorHandler }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
